// Browser-rendered subtitles for scripts that need text shaping (Devanagari conjuncts, Arabic joining, Thai ...).
// The ffmpeg.wasm build's libass has no HarfBuzz, so it draws one glyph per character ("नमस्ते" came out with a
// visible halant). The browser's canvas shapes text correctly, so each cue is drawn there into a transparent
// PNG strip and ffmpeg overlays the strips on the video at the right times.
import { scriptsInText, scriptFontUrl, scriptFamily } from "@/lib/scriptFonts";
import type { Cue, SubtitleStyle } from "./subtitles";

/** True when any cue uses a script that libass cannot shape without HarfBuzz. */
export function needsBrowserShaping(cues: Cue[]): boolean {
  return scriptsInText(cues.map((c) => c.text).join("\n")).some((s) => s.id !== "Hebrew");
}

export const BAND_FRACTION = 0.4;

export function bandGeometry(width: number, height: number, position: SubtitleStyle["position"]) {
  const h = Math.round((height * BAND_FRACTION) / 2) * 2;
  const y = position === "top" ? 0 : position === "middle" ? Math.round((height - h) / 2 / 2) * 2 : height - h;
  return { w: width, h, y };
}

async function ensureFonts(text: string) {
  const fams: string[] = [];
  const robotoUrl = "/fonts/Roboto-Regular.ttf";
  const loads: Promise<unknown>[] = [];
  const add = (family: string, url: string) => {
    fams.push(family);
    if (!Array.from(document.fonts as unknown as Iterable<FontFace>).some((f) => f.family.replace(/"/g, "") === family && f.status === "loaded")) {
      const ff = new FontFace(family, `url(${url})`);
      loads.push(ff.load().then((f) => document.fonts.add(f)));
    }
  };
  add("Roboto", robotoUrl);
  for (const s of scriptsInText(text)) add(scriptFamily(s), scriptFontUrl(s.file));
  await Promise.all(loads);
  return fams;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const para of text.split("\n")) {
    const words = para.trim().split(/\s+/);
    let cur = "";
    for (const w of words) {
      const t = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(t).width > maxW && cur) { out.push(cur); cur = w; } else cur = t;
    }
    out.push(cur);
  }
  return out;
}

/** Draws every cue to a transparent PNG strip; returns the PNG bytes (index-aligned with cues) and an empty strip. */
export async function renderCueStrips(cues: Cue[], width: number, height: number, style: SubtitleStyle): Promise<{ strips: Uint8Array[]; blank: Uint8Array; geo: ReturnType<typeof bandGeometry> }> {
  const clean = (t: string) => t.replace(/<[^>]*>/g, "").trim();
  const fams = await ensureFonts(cues.map((c) => c.text).join("\n"));
  const geo = bandGeometry(width, height, style.position);
  const fs = Math.max(14, Math.round(Math.min(width, height) * style.sizeFraction));
  const stack = fams.map((f) => `"${f}"`).concat("sans-serif").join(", ");
  const canvas = document.createElement("canvas");
  canvas.width = geo.w; canvas.height = geo.h;
  const ctx = canvas.getContext("2d")!;
  const toPng = () => new Promise<Uint8Array>((res, rej) => canvas.toBlob(async (b) => (b ? res(new Uint8Array(await b.arrayBuffer())) : rej(new Error("Could not draw the subtitles"))), "image/png"));

  ctx.clearRect(0, 0, geo.w, geo.h);
  const blank = await toPng();

  const strips: Uint8Array[] = [];
  const margin = Math.round(height * 0.05);
  for (const c of cues) {
    ctx.clearRect(0, 0, geo.w, geo.h);
    ctx.font = `400 ${fs}px ${stack}`;
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    const lines = wrapLines(ctx, clean(c.text), geo.w * 0.9);
    const lh = fs * 1.35;
    const blockH = lines.length * lh;
    let top = style.position === "top" ? margin : style.position === "middle" ? (geo.h - blockH) / 2 : geo.h - margin - blockH;
    top = Math.max(0, top);
    if (style.background) {
      const maxW = Math.max(...lines.map((l) => ctx.measureText(l).width));
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(geo.w / 2 - maxW / 2 - fs * 0.4, top - fs * 0.1, maxW + fs * 0.8, blockH + fs * 0.2);
    }
    ctx.lineJoin = "round"; ctx.lineWidth = Math.max(2, fs * 0.12); ctx.strokeStyle = "#000";
    ctx.fillStyle = style.color === "yellow" ? "#ffff00" : "#ffffff";
    lines.forEach((l, i) => {
      const y = top + i * lh + fs * 1.0;
      ctx.strokeText(l, geo.w / 2, y); ctx.fillText(l, geo.w / 2, y);
    });
    strips.push(await toPng());
  }
  return { strips, blank, geo };
}

/** Concat-demuxer list: blank strip in the gaps, each cue's strip for its duration. */
export function buildStripList(cues: Cue[]): string {
  const lines: string[] = [];
  let t = 0;
  const entry = (file: string, dur: number) => { lines.push(`file '${file}'`, `duration ${dur.toFixed(3)}`); };
  const sorted = cues.map((c, i) => ({ c, i })).sort((a, b) => a.c.start - b.c.start);
  for (const { c, i } of sorted) {
    const start = Math.max(c.start, t);
    if (start > t) entry("blank.png", start - t);
    if (c.end > start) { entry(`strip${i}.png`, c.end - start); t = c.end; }
  }
  lines.push("file 'blank.png'");
  return lines.join("\n") + "\n";
}
